import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  FileText, MapPin, Clock, DollarSign, Package, ChevronRight, 
  AlertCircle, Send, Eye, Users, Building, Lock, Star, CheckCircle
} from 'lucide-react';
import { rfqsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { UpgradePopup } from '../components/MembershipGating';

const RFQDetailPage = () => {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseOpen, setResponseOpen] = useState(false);
  const [responseData, setResponseData] = useState({ message: '', price_quote: '', lead_time: '', moq: '' });
  const [sending, setSending] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [matchingSuppliers, setMatchingSuppliers] = useState([]);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await rfqsApi.getById(rfqId);
        setRfq(data);

        // Fetch quotes if authenticated
        if (isAuthenticated) {
          try {
            const quotesRes = await rfqsApi.getQuotes(rfqId);
            setQuotes(quotesRes.data.quotes || []);
          } catch {}
          try {
            const matchRes = await rfqsApi.getMatchingSuppliers(rfqId);
            setMatchingSuppliers(matchRes.data.matching_suppliers || []);
          } catch {}
        }
      } catch {
        toast.error('RFQ not found');
        navigate('/rfqs');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [rfqId, navigate, isAuthenticated]);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/rfqs/${rfqId}` } } });
      return;
    }

    if (user?.user_type !== 'supplier') {
      toast.error('Only suppliers can send quotes');
      return;
    }

    // Check if free user
    if (user?.membership === 'free' || !user?.membership) {
      setShowUpgrade(true);
      return;
    }

    setSending(true);
    try {
      const result = await rfqsApi.submitQuote(rfqId, {
        message: responseData.message,
        price_quote: responseData.price_quote ? parseFloat(responseData.price_quote) : 0,
        price_unit: 'USD',
        lead_time: responseData.lead_time || '',
        moq: responseData.moq ? parseInt(responseData.moq) : 0
      });

      if (result.data.upgrade_needed) {
        setShowUpgrade(true);
        return;
      }

      toast.success('Quote submitted successfully!');
      setResponseOpen(false);
      setResponseData({ message: '', price_quote: '', lead_time: '', moq: '' });
      const quotesRes = await rfqsApi.getQuotes(rfqId);
      setQuotes(quotesRes.data.quotes || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-main py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-8" />
            <div className="bg-white border p-6">
              <div className="h-6 bg-slate-200 rounded w-2/3 mb-4" />
              <div className="h-4 bg-slate-200 rounded w-full mb-2" />
              <div className="h-4 bg-slate-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!rfq) return null;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container-main py-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-700">Home</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <Link to="/rfqs" className="text-slate-500 hover:text-slate-700">Buy Offers</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium truncate">{rfq.title}</span>
          </nav>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-[#0A192F] tracking-tight" data-testid="rfq-title">
                      {rfq.title}
                    </h1>
                    {rfq.is_urgent && (
                      <Badge className="bg-red-50 text-red-700 border-red-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Urgent
                      </Badge>
                    )}
                  </div>
                  <Badge variant="outline">{rfq.category}</Badge>
                </div>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-slate-500 mb-6">
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Posted {formatDate(rfq.created_at)}</span>
                <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{rfq.views} views</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4" />{rfq.responses_count} quotes</span>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-[#0A192F] mb-2">Product Required</h3>
                  <p className="text-slate-700">{rfq.product_name}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#0A192F] mb-2">Description</h3>
                  <p className="text-slate-600 whitespace-pre-line">{rfq.description}</p>
                </div>
                {rfq.specifications && (
                  <div>
                    <h3 className="font-semibold text-[#0A192F] mb-2">Specifications</h3>
                    <p className="text-slate-600 whitespace-pre-line">{rfq.specifications}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Supplier Quotes */}
            {quotes.length > 0 && (
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-[#0A192F] mb-4">
                  Supplier Quotes ({quotes.length})
                </h2>
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <div key={quote.response_id} className="border border-slate-200 p-4 relative">
                      {quote.contact_locked && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">
                            <Lock className="w-3 h-3 mr-1" />
                            Locked
                          </Badge>
                        </div>
                      )}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0A192F] flex items-center justify-center text-white font-bold text-sm">
                            {quote.supplier_name?.charAt(0) || 'S'}
                          </div>
                          <div>
                            <p className="font-medium text-[#0A192F]">
                              {quote.contact_locked ? quote.supplier_name : quote.supplier_name}
                            </p>
                            {quote.supplier_membership && quote.supplier_membership !== 'free' && (
                              <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] mt-0.5">
                                <Star className="w-3 h-3 mr-0.5" />{quote.supplier_membership}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {quote.price_quote > 0 && (
                          <span className="text-lg font-bold text-[#EA580C]">
                            ${quote.price_quote.toFixed(2)}/{rfq.quantity_unit?.replace(/s$/, '')}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 text-sm mb-3">{quote.message}</p>
                      <div className="flex gap-4 text-xs text-slate-500">
                        {quote.lead_time && <span>Lead time: {quote.lead_time}</span>}
                        {quote.moq > 0 && <span>MOQ: {quote.moq} units</span>}
                      </div>
                      {quote.contact_locked && (
                        <Button
                          onClick={() => navigate('/membership')}
                          variant="outline"
                          className="mt-3 text-xs h-8 border-[#EA580C] text-[#EA580C]"
                          data-testid="unlock-contact-btn"
                        >
                          <Lock className="w-3 h-3 mr-1" />
                          Upgrade to View Full Contact
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matching Suppliers */}
            {matchingSuppliers.length > 0 && (
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-[#0A192F] mb-4">
                  Matching Suppliers for This RFQ
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {matchingSuppliers.slice(0, 4).map((supplier) => (
                    <Link
                      key={supplier.user_id}
                      to={`/suppliers/${supplier.user_id}`}
                      className="flex items-center gap-3 p-3 border border-slate-200 hover:border-[#0A192F]/20 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#0A192F] flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {supplier.company_name?.charAt(0) || 'S'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[#0A192F] text-sm truncate">{supplier.company_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span>{supplier.country}</span>
                          {supplier.is_verified && (
                            <span className="flex items-center text-blue-600"><CheckCircle className="w-3 h-3 mr-0.5" />Verified</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-[#0A192F] mb-4">Requirements</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Quantity</p>
                    <p className="font-medium text-[#0A192F]">{rfq.quantity} {rfq.quantity_unit}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Destination</p>
                    <p className="font-medium text-[#0A192F]">{rfq.destination_country}</p>
                  </div>
                </div>
                {rfq.budget_max && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Budget</p>
                      <p className="font-medium text-[#0A192F]">${rfq.budget_min?.toFixed(0)} - ${rfq.budget_max?.toFixed(0)} {rfq.budget_currency}</p>
                    </div>
                  </div>
                )}
                {rfq.deadline && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Deadline</p>
                      <p className="font-medium text-[#0A192F]">{formatDate(rfq.deadline)}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 my-4 pt-4">
                <div className="text-sm text-slate-600">
                  <p><strong>Payment Terms:</strong> {rfq.payment_terms || 'To be discussed'}</p>
                  <p className="mt-2"><strong>Delivery Terms:</strong> {rfq.delivery_terms || 'To be discussed'}</p>
                </div>
              </div>

              {/* Submit Quote Button */}
              {user?.user_type === 'supplier' && (
                <Dialog open={responseOpen} onOpenChange={setResponseOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-[#EA580C] hover:bg-[#C2410C] rounded-sm font-semibold" data-testid="rfq-respond-btn">
                      <Send className="w-4 h-4 mr-2" />
                      Send Quote
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Submit Your Quote</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleQuoteSubmit} className="space-y-4 mt-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Price Quote (per unit) *</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={responseData.price_quote}
                          onChange={(e) => setResponseData({ ...responseData, price_quote: e.target.value })}
                          placeholder="e.g., 15.50"
                          className="mt-1"
                          required
                          data-testid="quote-price-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Lead Time</label>
                          <Input
                            value={responseData.lead_time}
                            onChange={(e) => setResponseData({ ...responseData, lead_time: e.target.value })}
                            placeholder="e.g., 7-14 days"
                            className="mt-1"
                            data-testid="quote-leadtime-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">MOQ</label>
                          <Input
                            type="number"
                            value={responseData.moq}
                            onChange={(e) => setResponseData({ ...responseData, moq: e.target.value })}
                            placeholder="e.g., 100"
                            className="mt-1"
                            data-testid="quote-moq-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Message *</label>
                        <Textarea
                          value={responseData.message}
                          onChange={(e) => setResponseData({ ...responseData, message: e.target.value })}
                          placeholder="Introduce your company and explain how you can fulfill this requirement..."
                          rows={4}
                          required
                          className="mt-1"
                          data-testid="quote-message-input"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-[#0A192F] hover:bg-[#172A46] rounded-sm font-semibold" disabled={sending} data-testid="quote-submit-btn">
                        {sending ? 'Submitting...' : 'Submit Quote'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              {/* Non-supplier CTA */}
              {!isAuthenticated && (
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#0A192F] hover:bg-[#172A46] rounded-sm font-semibold"
                  data-testid="rfq-login-to-quote"
                >
                  Sign In to Respond
                </Button>
              )}
            </div>

            {/* Buyer Info */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-[#0A192F] mb-4">Posted By</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <Building className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-[#0A192F]">Buyer from {rfq.buyer_country}</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Lock className="w-3 h-3" /> Company details protected</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UpgradePopup isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="rfq_view" />
      <Footer />
    </div>
  );
};

export default RFQDetailPage;
